import mixpanel from 'mixpanel-browser'
import { checkSessionSampling, shouldOverrideSampling } from './utilities'
import { PRODUCT_NAME, SESSION_SAMPLED_KEY } from './constant'

// This variable will hold the initialized mixpanel instance.
// It's a singleton pattern: subsequent imports of this module will reference the same `_globalMixpanelInstance`.
let _globalMixpanelInstance: typeof mixpanel | null = null
// Stores the session sampling status (true/false)
let _currentSessionIsSampled: boolean | null = null
// Stores the configured sample rate
let _currentTracesSampleRate: number | undefined = undefined

/**
 * This interface defines the structure of queued calls to Mixpanel.
 * It allows us to queue calls to Mixpanel methods before the instance is initialized.
 * This is useful for ensuring that events are not lost if they are called before Mixpanel.init() completes.
 * Each queued call contains the method name and its arguments.
 * This allows us to replay these calls once the Mixpanel instance is available.
 */
interface QueuedCall {
  method: 'track' | 'identify' | 'people.set' | 'people.increment' | 'alias' | 'reset'
  args: any[]
}
const _callQueue: QueuedCall[] = []

/**
 * Internal helper to either execute a Mixpanel call immediately or queue it.
 * This function also applies sampling logic and adds default properties for 'track' calls.
 */
const callMixpanelOrQueue = (method: QueuedCall['method'], args: any[]) => {
  // --- Apply Sampling and Product Property for 'track' method ---
  if (method === 'track') {
    const eventName = args[0] as string
    let properties = args[1] as Record<string, any> | undefined

    // Determine if this event should be tracked based on session sampling and overrides
    const isSessionSampled = _currentSessionIsSampled === true // Get the session's determined status
    const overridesSampling = shouldOverrideSampling(eventName)
    const shouldTrack = isSessionSampled || overridesSampling

    if (!shouldTrack) {
      console.warn(
        `MixpanelGlobal: Event "${eventName}" NOT tracked due to session sampling 
        (sampled: ${isSessionSampled}, override: ${overridesSampling}).`,
      )
      return // Stop here if the event should not be tracked
    }

    // If tracking, add the Product property
    properties = { ...properties, Product: PRODUCT_NAME }
    args[1] = properties // Update the arguments with the modified properties
  }

  if (_globalMixpanelInstance) {
    // Mixpanel is initialized, execute the call directly
    try {
      if (method === 'track') {
        _globalMixpanelInstance.track(...(args as [string, any?]))
      } else if (method === 'identify') {
        _globalMixpanelInstance.identify(args[0])
        if (args[1]) _globalMixpanelInstance.people.set(args[1])
      } else if (method === 'people.set') {
        _globalMixpanelInstance.people.set(...(args as [string, any?]))
      } else if (method === 'people.increment') {
        _globalMixpanelInstance.people.increment(...(args as [string, any?]))
      } else if (method === 'alias') {
        _globalMixpanelInstance.alias(...(args as [string, any?]))
      } else if (method === 'reset') {
        _globalMixpanelInstance.reset()
        // Clear session sampling decision from localStorage on reset
        localStorage.removeItem(SESSION_SAMPLED_KEY)
        _currentSessionIsSampled = null // Reset internal state
      }
    } catch (error) {
      console.error(`Mixpanel: Error calling method "${method}" with args`, args, error)
    }
  } else {
    console.warn(`Mixpanel: Queueing call for method "${method}". Mixpanel not initialized yet.`)
    _callQueue.push({ method, args })
  }
}

/**
 * Sets the global Mixpanel instance and configures sampling.
 * This function should ONLY be called by `MixpanelProvider` after `mixpanel.init()`.
 * @param instance The initialized mixpanel-browser instance, or `null`.
 */
export const setGlobalMixpanelInstance = (instance: typeof mixpanel | null, tracesSampleRate: number | undefined) => {
  _globalMixpanelInstance = instance
  _currentTracesSampleRate = tracesSampleRate

  // Crucially, determine session sampling status here, once per session
  if (
    (_currentTracesSampleRate !== undefined || localStorage.getItem(SESSION_SAMPLED_KEY) !== undefined) &&
    _currentTracesSampleRate !== null
  ) {
    if (typeof _currentTracesSampleRate === 'number') {
      checkSessionSampling(_currentTracesSampleRate)
    }
  }
  _currentSessionIsSampled = localStorage.getItem(SESSION_SAMPLED_KEY) === 'true' || false

  if (_globalMixpanelInstance && _callQueue.length > 0) {
    // It's important to copy and then clear the queue to avoid issues during flushing
    const callsToFlush = [..._callQueue]
    _callQueue.length = 0 // Clear the original queue
    callsToFlush.forEach((queued) => {
      callMixpanelOrQueue(queued.method, queued.args) // Re-run through the safe caller
    })
  }
}

/**
 * Retrieves the global Mixpanel instance.
 * Use with caution, as it might be `null` if Mixpanel hasn't been initialized yet.
 * @returns The initialized mixpanel-browser instance or `null`.
 */
export const getGlobalMixpanelInstance = (): typeof mixpanel | null => {
  return _globalMixpanelInstance
}

/**
 * Tracks an event using the globally available Mixpanel instance.
 * If Mixpanel is not yet initialized, a warning will be logged, and the event will not be tracked.
 * This function provides a robust way to track events anywhere in your app.
 * @param eventName The name of the event to track.
 * @param properties Optional properties to send with the event.
 */
export const trackMixpanelEvent = (eventName: string, properties?: Record<string, any>) => {
  callMixpanelOrQueue('track', [eventName, properties])
}

/**
 * Identifies a user using the globally available Mixpanel instance.
 * If Mixpanel is not yet initialized, a warning will be logged, and the user will not be identified.
 * This function provides a robust way to identify users anywhere in your app.
 * @param email The email address of the user to identify.
 * @param properties Optional properties to set for the user.
 */
export const identifyMixpanelUser = (email: string, properties?: Record<string, any>) => {
  if (email && email !== 'anonymous') {
    callMixpanelOrQueue('identify', [email])
    if (properties) {
      // If properties are provided, set them in Mixpanel People
      callMixpanelOrQueue('people.set', [properties])
    }
  }
}

/**
 * Sets properties for the current user's Mixpanel People profile.
 * Will log a warning if Mixpanel has not been initialized or if the people API is not available.
 * @param properties The properties to set for the current user.
 */
export const setMixpanelUserProperties = (properties: Record<string, any>) => {
  callMixpanelOrQueue('people.set', [properties])
}

/**
 * Increments properties for the current user's Mixpanel People profile.
 * This is useful for tracking counts or metrics that should be incremented.
 * Will log a warning if Mixpanel has not been initialized or if the people API is not available.
 * @param properties The properties to increment for the current user.
 */
export const incrementMixpanelUserProperties = (properties: Record<string, number>) => {
  callMixpanelOrQueue('people.increment', [properties])
}

/**
 * Aliases a new user ID with an old user ID in Mixpanel.
 * This is useful for merging user identities when a user changes their ID.
 * Will log a warning if Mixpanel has not been initialized.
 * @param newId The new user ID to alias.
 * @param oldId The old user ID to merge with the new ID.
 */
export const aliasMixpanelUser = (newId: string, oldId: string) => {
  callMixpanelOrQueue('alias', [newId, oldId])
}

/**
 * Resets the Mixpanel instance, clearing the current user identity.
 * This is useful for logging out users or resetting the Mixpanel state.
 * Will log a warning if Mixpanel has not been initialized.
 */
export const resetMixpanel = () => {
  callMixpanelOrQueue('reset', [])
}
